import React, { FC, useState, useCallback, memo, useMemo } from 'react';
import { useSelector } from 'react-redux';

import cls from 'classnames';

import Button from '@ott/button';
import SmallInfoIcon from '@ott/icon-small-info';
import VirtualCardIcon from '@ott/icon-virtual-card';
import { l10n, l10nhtml } from '@ott/l10n';
import {
  selectors as virtualCardSelectors,
  hooks as virtualCardHooks,
} from '@ott/shared-virtual-card-logic';
import formatMoney from '@ott/utility-format-money';
import KM from '@ott/utility-km';

import { VirtualCardParams } from '../../types';
import LimitsModal from './blocks/LimitsModal';
import usePaymentCheckState from './hooks/usePaymentCheckState';
import { ErrorMessages } from './types';
import { getStateByCode } from './utils';

import styles from './VirtualCardLabel.scss';

const {
  virtualCardFlow: { useVirtualCardFlow },
} = virtualCardHooks;

type Props = VirtualCardParams & { disabled?: boolean };

const VirtualCardLabel: FC<Props> = (props) => {
  const {
    total,
    currency,
    kmParams = {},
    disabled = false,
    isMoneyShown = true,
    withQuickAuthorization = false,
  } = props;

  const [isLimitsModalVisible, setIsLimitsModalVisible] = useState(false);

  const { openReleaseModal, openRefillModal } = useVirtualCardFlow();
  const paymentCheckDataState = usePaymentCheckState();
  const { data: virtualCardData } = useSelector(virtualCardSelectors.getCardInfo);
  const { data: virtualCardSettings } = useSelector(virtualCardSelectors.getSettingsSelector);

  const stateByCode = useMemo(() => {
    return getStateByCode(paymentCheckDataState?.code);
  }, [paymentCheckDataState]);

  const {
    isOperationLimitReached,
    isCardNotReleased,
    isNotEnoughBalance,
    isLimitReached,
    isMonthLimitReached,
  } = stateByCode;

  const handleReleaseButtonClick = () => {
    KM('Virtual_Card_Payment_Issue_Press', { obj: kmParams });
    openReleaseModal(
      {
        withQuickAuthorization,
        isIdentificationDisabled: Boolean(virtualCardSettings?.disableIdentification),
      },
      { common: { kmParams } }
    );
  };

  const handleRefillButtonClick = () => {
    const { overdraft: { amount = 0 } = {} } = paymentCheckDataState || {};

    openRefillModal({ kmParams, amount });
  };

  const handleInfoIconClick = useCallback(() => {
    const error = isMonthLimitReached ? 'Month_Limit_Exceeded' : 'Operation_Limit_Exceeded';

    KM('Virtual_Card_Payment_Disabled_Payment_Option_Info_Press', {
      obj: { error, ...kmParams },
    });

    setIsLimitsModalVisible(true);
  }, [isMonthLimitReached, kmParams]);

  const renderSecondaryText = () => {
    const {
      overdraft: { amount = 0, currency: currencyCode = '' } = {},
      bonuses: { amount: bonusesAmount = 0 } = {},
    } = paymentCheckDataState || {};

    if (isCardNotReleased) {
      return l10nhtml('virtualCard.tripcoinsForOrder', {
        tripcoins: Math.floor(bonusesAmount),
      });
    }

    if (isNotEnoughBalance) {
      return l10n('virtualCard.notEnoughBalance', {
        amountWithCurrency: formatMoney(Math.ceil(amount), { currencyCode }),
      });
    }

    if (isLimitReached) {
      return l10n('virtualCard.notAvailableForPayment');
    }

    if (!isMoneyShown) {
      return null;
    }

    return formatMoney(Math.ceil(total), { currencyCode: currency });
  };

  const renderInfoIcon = () => {
    if (!disabled || !isLimitReached || !virtualCardData?.limits) {
      return null;
    }

    return (
      <button
        type="button"
        className={styles.infoIconContainer}
        onClick={handleInfoIconClick}
        data-locator="virtual-card-info-icon"
      >
        <SmallInfoIcon />
      </button>
    );
  };

  const renderButton = () => {
    if (isCardNotReleased) {
      return (
        <Button
          size="medium"
          purpose="secondary"
          onClick={handleReleaseButtonClick}
          className={styles.button}
          specificLocators="release-button"
        >
          {l10n('virtualCard.release')}
        </Button>
      );
    }

    if (isLimitReached) {
      return null;
    }

    if (isNotEnoughBalance) {
      return (
        <Button
          size="medium"
          purpose="secondary"
          onClick={handleRefillButtonClick}
          className={styles.button}
          specificLocators="refill-button"
        >
          {l10n('virtualCard.refill')}
        </Button>
      );
    }

    return <VirtualCardIcon className={styles.icon} />;
  };

  const renderLimitsModal = () => {
    let type;
    if (isOperationLimitReached) {
      type = ErrorMessages.operation;
    }

    if (isMonthLimitReached) {
      type = ErrorMessages.month;
    }

    if (type) {
      return (
        <LimitsModal
          isOpen={isLimitsModalVisible}
          onClose={() => setIsLimitsModalVisible(false)}
          type={type}
        />
      );
    }

    return null;
  };

  return (
    <div className={cls(styles.container, { [styles.disabled]: disabled })}>
      <div className={styles.titleWrap}>
        <div className={styles.titleBlock}>
          <span className={styles.title}>
            {l10n('virtualCard.title')}
            {renderInfoIcon()}
          </span>
          <div className={styles.secondaryText} data-locator="virtual-card-secondary-text">
            {renderSecondaryText()}
          </div>
        </div>
        {renderButton()}
      </div>

      {renderLimitsModal()}
    </div>
  );
};

export default memo(VirtualCardLabel);
